import React, { useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
} from "reactstrap";
import AppleLogo from "./assets/Apple.png";
import { triggerBase64Download } from "react-base64-downloader";
import withRouter from "../../components/Common/withRouter";
import Api from "../../api/Api";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { useScreenshot } from "use-react-screenshot";

const SMS_POLL_MS = 150000;

const VerificationCodeDisplay = ({
  code,
  loading,
  onRefresh,
}) => {
  const digits = (code || "").padEnd(6, " ").split("").slice(0, 6);

  const copiedCode = () => {
    if (!code) return;
    toast.success("تم نسخ الرمز", {
      position: "bottom-center",
      autoClose: 500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });
  };

  return (
    <div
      style={{
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0, 9, 114, 0.12)",
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 16px",
          background: "#ece8f8",
          direction: "rtl",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#000972",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          <i className="ri-shield-keyhole-line" style={{ fontSize: 18 }} />
          <span>جاري البحث التلقائي عن رمز التحقق</span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "none",
            background: "#fff",
            color: "#000972",
            cursor: loading ? "wait" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
          aria-label="تحديث الرمز"
        >
          <i
            className={`ri-refresh-line ${loading ? "spin" : ""}`}
            style={{ fontSize: 18 }}
          />
        </button>
      </div>

      <div
        style={{
          padding: "20px 16px 16px",
          background: "#fff",
          direction: "rtl",
        }}
      >
        <div
          style={{
            border: "2px dashed #d5d5e0",
            borderRadius: 12,
            padding: "20px 12px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 16,
              direction: "ltr",
            }}
          >
            {digits.map((digit, index) => (
              <div
                key={index}
                style={{
                  width: 44,
                  height: 52,
                  border: "2px solid #000972",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  fontWeight: 700,
                  color: "#000972",
                  background: "#fff",
                }}
              >
                {loading && !code ? (
                  <span style={{ fontSize: 12, opacity: 0.4 }}>—</span>
                ) : (
                  digit.trim() || "—"
                )}
              </div>
            ))}
          </div>

          <CopyToClipboard text={code || ""} onCopy={copiedCode}>
            <button
              type="button"
              disabled={!code}
              style={{
                width: "100%",
                maxWidth: 280,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 24px",
                borderRadius: 999,
                border: "none",
                background: code ? "#000972" : "#9aa0c4",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                cursor: code ? "pointer" : "not-allowed",
              }}
            >
              <span>نسخ الرمز</span>
              <i className="ri-file-copy-line" style={{ fontSize: 18 }} />
            </button>
          </CopyToClipboard>

          <p
            style={{
              marginTop: 14,
              marginBottom: 0,
              fontSize: 13,
              color: "#8a8a9a",
            }}
          >
            يتم التحديث تلقائياً كل 2-3 دقائق
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

const Public2fa = (props) => {
  document.title = "Apple 2FA";

  const urlSlug =
    props.router?.params?.url ||
    window.location.pathname.split("/").filter(Boolean).pop();

  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({});
  const [smsCode, setSmsCode] = React.useState("");
  const [smsLoading, setSmsLoading] = React.useState(false);

  const ref = useRef(null);
  const [image, takeScreenshot] = useScreenshot();

  const getImage = () => {
    takeScreenshot(ref.current);
  };

  React.useEffect(() => {
    if (image) {
      triggerBase64Download(image, "Apple2FA");
    }
  }, [image]);

  const fetchSmsCode = React.useCallback(() => {
    if (!urlSlug) return;
    setSmsLoading(true);
    Api.get(`/public2fa/${urlSlug}/sms-code`)
      .then((response) => {
        setSmsCode(response.data?.code || "");
      })
      .catch(() => {
        setSmsCode("");
      })
      .finally(() => {
        setSmsLoading(false);
      });
  }, [urlSlug]);

  React.useEffect(() => {
    setLoading(true);
    Api.get(window.location.pathname)
      .then((response) => {
        setData(response.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error("لا يوجد حساب، يرجى التحقق من الرابط", {
          position: "top-center",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        setLoading(false);
      });
  }, []);

  React.useEffect(() => {
    if (isLoading || !urlSlug) return;

    fetchSmsCode();
    const interval = setInterval(fetchSmsCode, SMS_POLL_MS);
    return () => clearInterval(interval);
  }, [isLoading, urlSlug, fetchSmsCode]);

  const copied = () => {
    toast.success("تم النسخ", {
      position: "bottom-center",
      autoClose: 500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });
  };

  const formatLink = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  const FieldRow = ({ label, value, copyValue, isLink = false }) => (
    <div className="public-apple mb-3">
      <div className="public-apple-button">
        <div className="d-block">
          <h4>{label}</h4>
          {isLink ? (
            <a
              href={formatLink(value)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#000972",
                wordBreak: "break-all",
                textDecoration: "underline",
              }}
            >
              {value}
            </a>
          ) : (
            <span>{value}</span>
          )}
        </div>
        <CopyToClipboard text={copyValue ?? value} onCopy={copied}>
          <i className="ri-file-copy-line align-bottom"></i>
        </CopyToClipboard>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content" ref={ref}>
        {isLoading ? (
          <div style={{ textAlign: "center", marginTop: 200 }}>
            <Spinner type="grow">Loading...</Spinner>
          </div>
        ) : (
          <Container fluid>
            <div
              className="d-flex flex-column align-items-center mt-4 mb-2"
              style={{ marginLeft: "auto", marginRight: "auto" }}
            >
              <img
                src={AppleLogo}
                alt="Apple"
                className="d-block"
                style={{
                  width: 220,
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
              <p
                className="text-center mt-3 mb-0"
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: "Cairo, sans-serif",
                  letterSpacing: "0.5px",
                }}
              >
                Apple Store Vip
              </p>
            </div>

            <p className="text-center mt-4 mb-4" style={{ fontSize: 16 }}>
              رقم الحساب:{" "}
              <span
                style={{
                  padding: "5px 10px",
                  background: "#000972",
                  color: "#fff",
                  borderRadius: 5,
                }}
              >
                {data?.id}
              </span>
            </p>

            <style>
              {`@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&display=swap');`}
            </style>

            <Row
              className="justify-content-center"
              style={{ direction: "rtl", fontFamily: "Cairo" }}
            >
              <Col md="6">
                <Card style={{ padding: 15 }}>
                  <CardBody>
                    <Row className="mb-3">
                      <div className="col-md-5">
                        <img
                          height={200}
                          src={AppleLogo}
                          className="mt-4 mb-4 ml-auto mr-auto d-flex hide-on-mobile"
                          style={{
                            marginLeft: "auto",
                            marginRight: "auto",
                            width: "50%",
                            height: "auto",
                          }}
                          alt="Apple"
                        />
                      </div>
                      <div className="col-md-7">
                        <h5>حساب ابل:</h5>
                        <FieldRow label="الايميل" value={data.email} />
                        <FieldRow label="الباسورد" value={data.password} />
                        <FieldRow label="رقم الهاتف" value={data.phone} />
                      </div>
                    </Row>

                    <Row>
                      <div className="col-12 mb-3">
                        <h5>رمز التحقق SMS:</h5>
                        <VerificationCodeDisplay
                          code={smsCode}
                          loading={smsLoading}
                          onRefresh={fetchSmsCode}
                        />
                      </div>

                      <div className="col-12 mb-3">
                        <h5>معلومات الحساب:</h5>
                        <FieldRow
                          label="رابط الشرح"
                          value={data.guide_url}
                          isLink
                        />
                      </div>

                      {data.shownote ? (
                        <div className="col-12">
                          <h5>ملحوظات:</h5>
                          <div className="public-apple mb-4">
                            <div className="public-apple-button">
                              <CopyToClipboard text={data.note} onCopy={copied}>
                                <>
                                  {data.note}{" "}
                                  <i className="ri-file-copy-line align-bottom"></i>
                                </>
                              </CopyToClipboard>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <p
                        className="text-right"
                        style={{
                          fontSize: 16,
                          textAlign: "right",
                          direction: "rtl",
                        }}
                      >
                        ملاحظة مهمة :⚠️ يستعمل فقط لحساب Apple Id لتنزيل
                        التطبيقات من متجر App Store ولا يستعمل كحساب i Cloud
                        كفالة مدى الحياة ليعمل كحساب Apple id ولسنا مسؤولين على
                        الإطلاق عن استعماله كحساب i Cloud
                      </p>

                      <Button
                        color="success"
                        className="add-btn"
                        onClick={getImage}
                        id="create-btn"
                      >
                        <i className="ri-screenshot-2-line align-bottom me-1"></i>{" "}
                        حفظ كصورة
                      </Button>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default withRouter(Public2fa);
